import { NotUndefined } from 'app/tools/general.types';
import _ from 'lodash';
import { Dispatch, useCallback, useEffect, useRef } from 'react';
import {
  retrieveStoredItem,
  storeItem,
} from './local-storage-methods';
import { LSKey } from './local-storage.types';

export type ReadonlyRefObject<T> = { readonly current: T };
/** Use a ref object initially filled from, and backed up to, local
 * storage. Changing the ref is done via the setter function. */
export default function useLocalRef<
  D,
  T extends NotUndefined<D> = NotUndefined<D>
>(key: LSKey, defaultValue: T): [ReadonlyRefObject<T>, Dispatch<T>] {
  const value = useRef(retrieveStoredItem<T>(key) ?? defaultValue);

  const setItem = useCallback(
    (newValue: T) => {
      storeItem<T>(key, newValue);
      window.dispatchEvent(new Event('storage'));
    },
    [key]
  );

  const handleStorage = useCallback(() => {
    const newItem = retrieveStoredItem<T>(key);
    if (!_.isEqual(newItem, value.current)) {
      value.current = newItem ?? defaultValue;
    }
  }, [defaultValue, key]);

  useEffect(() => {
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [handleStorage]);

  return [value, setItem];
}
